import { useContext, useEffect } from 'react'
import { useParams } from 'react-router'
import {
  showErrorNotification,
  showSuccessNotification,
} from '@shared/helpers/notifications/notifications'
import {
  useAuthenticatedGraphMutation,
  useAuthenticatedGraphQuery,
} from '@/hooks/use-authenticated-query'
import { SetSpeciGql } from '@/graphql/mutations/speci/speci-upsert.graphql'
import { GetSpeciStationDataGql } from '@/graphql/queries/speci/get-speci-station-data.graphql'
import { GetSpeciGql } from '@/graphql/queries/speci/get-speci.graphql'
import { StationContext } from '@/pages/station-context'
import { useNavigation } from '@/hooks/use-navigation'
import { SpeciEditor, SpeciAction } from '@shared/components/speci/speci-editor/speci-editor'
import { SpeciData } from '@shared/components/speci/speci-types'
import { SubmitSpeciModal } from '@/components/speci/submit-speci-modal/submit-speci-modal'

interface CreateWoolSpecificationPageProps {
  action?: SpeciAction
}

export function CreateWoolSpecificationPage({
  action = SpeciAction.NEW,
}: CreateWoolSpecificationPageProps) {
  const { station } = useContext(StationContext)
  const { speciId } = useParams()
  const { navigateToListSpeci } = useNavigation()

  const navToSpeciHomepage = () => {
    navigateToListSpeci(station.recordNumber)
  }

  useEffect(() => {
    // If no station is selected, navigate to the station page
    if (!station) {
      showErrorNotification('Please select a station first', { autoClose: true })
      navToSpeciHomepage()
    }
  }, [station])

  useEffect(() => {
    // If no speciId is present in the URL, navigate to the speci homepage
    if (!speciId) {
      navToSpeciHomepage()
    }
  }, [speciId])

  // Fetch additional station data
  const { data: stationData, isLoading: stationDataLoading } = useAuthenticatedGraphQuery({
    queryKey: ['speciStationDetails', station.recordNumber],
    gql: GetSpeciStationDataGql,
    queryParams: { recordNumber: station.recordNumber },
    enabled: !!station.recordNumber,
  })

  // Fetch the wool specification data
  const {
    data: speciData,
    isFetching,
    isStale,
  } = useAuthenticatedGraphQuery({
    queryKey: ['getSpeci', speciId],
    gql: GetSpeciGql,
    queryParams: { speciId },
    enabled: !!action && action === SpeciAction.EDIT && !!speciId,
  })

  // Mutation to update the wool specification
  const updateSpeciMutation = useAuthenticatedGraphMutation({
    gql: SetSpeciGql,
    gqlKey: 'setSpeci',
  })

  const handleUpdateSpeci = async (
    speci: SpeciData,
    stationRecordNumber: string,
    submitPending: boolean
  ) => {
    try {
      await updateSpeciMutation.mutateAsync({
        speci,
        stationRecordNumber,
      })
      if (!submitPending) {
        showSuccessNotification('Wool Specification successfully updated')
      }
    } catch (error) {
      showErrorNotification('Failed to update wool specification', { autoClose: true })
      throw error
    }
  }

  if (!station || !speciId) {
    return null
  }

  return (
    <SpeciEditor
      action={action}
      station={station}
      stationDetailsForSpeci={stationData?.stationDetailsForSpeci ?? undefined}
      speciId={speciId}
      speciData={speciData?.speci?.speci ?? undefined}
      stationDataLoading={stationDataLoading}
      speciDataIsStale={isStale}
      speciDataIsFetching={isFetching}
      disableDuplicateLine
      onUpdateSpeci={handleUpdateSpeci}
      onNavigateToSpeciHomepage={navToSpeciHomepage}
      renderSubmitModal={({ isModalOpen, closeModal }) => (
        <SubmitSpeciModal
          speciId={speciId}
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          successNavigate={navToSpeciHomepage}
        />
      )}
      displayCertificationStatus={false}
    />
  )
}
